import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Collapse,
  Fade,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuth0 } from '@auth0/auth0-react';
import { ChatMessage } from '../../types/preparation';
import { toast } from 'react-hot-toast';
import { API_URLS } from '../../config';

interface FloatingChatInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  preparationId?: string; // Optional - if provided, chat is context-specific
}

export const FloatingChatInterface: React.FC<FloatingChatInterfaceProps> = ({
  isOpen,
  onClose,
  preparationId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsSending(true);

    try {
      const token = await getAccessTokenSilently();

      // Use preparation-specific endpoint if preparationId is provided
      const endpoint = preparationId
        ? `${API_URLS.BASE}/api/preparations/${preparationId}/chat`
        : `${API_URLS.BASE}/api/general-chat`; // General chat endpoint

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: inputMessage }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message. General chat coming soon!');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <Fade in={isOpen}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: isMinimized ? 320 : 400,
          height: isMinimized ? 60 : 'auto',
          maxHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          zIndex: 1300,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isMinimized ? 'pointer' : 'default',
          }}
          onClick={() => isMinimized && setIsMinimized(false)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {preparationId ? 'Role Prep Chat' : 'AI Interview Coach'}
              </Typography>
              {!isMinimized && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {preparationId ? 'Ask about this role' : 'General interview advice'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              sx={{ color: 'primary.contrastText', mr: 0.5 }}
            >
              <MinimizeIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                setMessages([]);
              }}
              sx={{ color: 'primary.contrastText' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Collapsed Content */}
        <Collapse in={!isMinimized}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Messages Area */}
            <Box
              sx={{
                minHeight: 200,
                maxHeight: 350,
                overflow: 'auto',
                p: 2,
                backgroundColor: 'action.hover',
              }}
            >
              {messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How can I help you today?
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {preparationId ? (
                      <>
                        <Chip
                          label="Common interview questions?"
                          size="small"
                          onClick={() => setInputMessage("What are common interview questions for this role?")}
                          sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                          label="Company culture insights?"
                          size="small"
                          onClick={() => setInputMessage("Tell me about the company culture")}
                          sx={{ cursor: 'pointer' }}
                        />
                      </>
                    ) : (
                      <>
                        <Chip
                          label="How to prepare for interviews?"
                          size="small"
                          onClick={() => setInputMessage("How should I prepare for technical interviews?")}
                          sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                          label="Tips for coding interviews?"
                          size="small"
                          onClick={() => setInputMessage("What are tips for coding interviews?")}
                          sx={{ cursor: 'pointer' }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box>
                  {messages.map((message, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                        mb: 1.5
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          maxWidth: '80%',
                          backgroundColor: message.role === 'user' ? 'primary.main' : 'background.paper',
                          color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            opacity: 0.7,
                            fontSize: '0.7rem'
                          }}
                        >
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  {isSending && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                      <Paper elevation={1} sx={{ p: 1.5 }}>
                        <CircularProgress size={16} />
                      </Paper>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* Input Area */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={3}
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSending}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isSending}
                  sx={{ minWidth: 50, px: 2 }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
};
